/**
 * Error Monitoring Dashboard
 * Simple admin interface for viewing errors and performance metrics
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ErrorMonitoringDashboard = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('errors');
  const [filter, setFilter] = useState({ level: '', type: '' });

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch errors
      const errorsRes = await axios.get('/api/admin/errors', {
        params: {
          level: filter.level || undefined,
          error_type: filter.type || undefined,
          limit: 50,
        },
      });
      setErrors(errorsRes.data.errors || []);

      // Fetch stats
      const statsRes = await axios.get('/api/admin/errors/stats', {
        params: { hours: 24 },
      });
      setStats(statsRes.data);

      // Fetch performance metrics
      const perfRes = await axios.get('/api/admin/performance/metrics', {
        params: { hours: 24 },
      });
      setPerformanceMetrics(perfRes.data.metrics || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🐾 Happy Paws Error Monitoring
          </h1>
          <p className="text-gray-600">
            Real-time error tracking and performance monitoring
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Total Errors (24h)
              </h3>
              <p className="text-3xl font-bold text-gray-900">
                {stats.total_errors}
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Unique Error Types
              </h3>
              <p className="text-3xl font-bold text-gray-900">
                {stats.by_type?.length || 0}
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Affected Endpoints
              </h3>
              <p className="text-3xl font-bold text-gray-900">
                {stats.by_endpoint?.length || 0}
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('errors')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'errors'
                    ? 'border-b-2 border-orange-500 text-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Recent Errors
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'stats'
                    ? 'border-b-2 border-orange-500 text-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Error Statistics
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`py-4 px-6 font-medium text-sm ${
                  activeTab === 'performance'
                    ? 'border-b-2 border-orange-500 text-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Performance
              </button>
            </nav>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading...</p>
              </div>
            ) : (
              <>
                {/* Recent Errors Tab */}
                {activeTab === 'errors' && (
                  <div>
                    {/* Filters */}
                    <div className="flex gap-4 mb-4">
                      <select
                        value={filter.level}
                        onChange={(e) => setFilter({ ...filter, level: e.target.value })}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="">All Levels</option>
                        <option value="error">Error</option>
                        <option value="warning">Warning</option>
                        <option value="info">Info</option>
                      </select>
                      
                      <select
                        value={filter.type}
                        onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="">All Types</option>
                        <option value="backend">Backend</option>
                        <option value="frontend">Frontend</option>
                      </select>
                      
                      <button
                        onClick={fetchData}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                      >
                        Refresh
                      </button>
                    </div>

                    {/* Errors List */}
                    <div className="space-y-4">
                      {errors.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          No errors found. Everything is running smoothly! 🎉
                        </p>
                      ) : (
                        errors.map((error) => (
                          <div
                            key={error.id}
                            className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <span
                                  className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                                    error.level === 'error'
                                      ? 'bg-red-100 text-red-800'
                                      : error.level === 'warning'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}
                                >
                                  {error.level.toUpperCase()}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {error.error_type}
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-600">
                                  Count: {error.count}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Last seen: {formatTimestamp(error.last_seen)}
                                </div>
                              </div>
                            </div>
                            
                            <h3 className="font-medium text-gray-900 mb-1">
                              {error.exception_type}
                            </h3>
                            
                            <p className="text-sm text-gray-700 mb-2">
                              {error.message}
                            </p>
                            
                            {error.context?.endpoint && (
                              <div className="text-xs text-gray-500 mb-2">
                                <span className="font-medium">Endpoint:</span>{' '}
                                {error.context.method} {error.context.endpoint}
                              </div>
                            )}
                            
                            {error.stack_trace && (
                              <details className="mt-2">
                                <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                                  View Stack Trace
                                </summary>
                                <pre className="mt-2 p-2 bg-gray-900 text-green-400 text-xs rounded overflow-x-auto">
                                  {error.stack_trace}
                                </pre>
                              </details>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Statistics Tab */}
                {activeTab === 'stats' && stats && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Top Error Types (Last 24 hours)
                    </h3>
                    <div className="space-y-2 mb-8">
                      {stats.by_type?.map((item) => (
                        <div
                          key={item._id}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded"
                        >
                          <span className="font-medium">{item._id}</span>
                          <span className="text-gray-600">
                            {item.count} occurrences ({item.unique_occurrences} unique)
                          </span>
                        </div>
                      ))}
                    </div>

                    <h3 className="text-lg font-semibold mb-4">
                      Top Failing Endpoints
                    </h3>
                    <div className="space-y-2">
                      {stats.by_endpoint?.map((item) => (
                        <div
                          key={item._id}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded"
                        >
                          <span className="font-mono text-sm">{item._id}</span>
                          <span className="text-gray-600">
                            {item.count} errors
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Performance Tab */}
                {activeTab === 'performance' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Average Response Times (Last 24 hours)
                    </h3>
                    <div className="space-y-2">
                      {performanceMetrics.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          No performance data available yet.
                        </p>
                      ) : (
                        performanceMetrics.map((metric) => (
                          <div
                            key={metric._id}
                            className="flex justify-between items-center p-3 bg-gray-50 rounded"
                          >
                            <div>
                              <div className="font-mono text-sm">{metric._id}</div>
                              <div className="text-xs text-gray-500">
                                {metric.request_count} requests
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                Avg: {formatDuration(metric.avg_duration_ms)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Max: {formatDuration(metric.max_duration_ms)}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorMonitoringDashboard;
